import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSsoInit } from '@/api/hooks/useSso';

/**
 * /auth/sso/start — Initiates the OIDC SSO flow.
 * Extracts tenant slug from ?slug= query param (set by LoginPage when the user
 * enters their org identifier and clicks "Sign in with SSO").
 * Falls back to a manual "Company identifier" input when slug is absent.
 */
export function SsoStartPage() {
  const params = new URLSearchParams(window.location.search);
  const [slug, setSlug] = useState(params.get('slug') ?? '');
  const returnTo = params.get('return_to') ?? undefined;

  const initMutation = useSsoInit();

  useEffect(() => {
    if (slug && !initMutation.isPending && !initMutation.data && !initMutation.error) {
      initMutation.mutate(
        { tenant_slug: slug, return_to: returnTo },
        {
          onSuccess: (data) => {
            window.location.href = data.authorization_url;
          },
        },
      );
    }
  // Only run on mount if slug is pre-filled
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!slug) return;
    initMutation.mutate(
      { tenant_slug: slug, return_to: returnTo },
      {
        onSuccess: (data) => {
          window.location.href = data.authorization_url;
        },
      },
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-12 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Single Sign-On</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {initMutation.isPending ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-accent-cyan" />
              <p className="text-sm text-text-secondary">Redirecting to your identity provider…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Sign in with SSO</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Enter your organisation identifier to continue
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="slug">Organisation identifier</Label>
                <Input
                  id="slug"
                  type="text"
                  placeholder="your-company"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().trim())}
                  autoFocus={!slug}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={!slug || initMutation.isPending}>
                Continue with SSO
              </Button>
              <a
                href="/login"
                className="block text-center text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                ← Back to sign in
              </a>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
